/**
 * "Made by Perilous Games" startup splash: plays the studio video fullscreen
 * while the mall builds behind it, then fades out. Skippable with a
 * click/tap or any key. The video autoplays muted (browsers block audible
 * autoplay).
 *
 * The overlay never lifts before the world is ready: main.ts calls
 * setReady() once the mall is built and connected. If the video ends (or is
 * skipped, or fails) first, the overlay switches to a "Loading Mall Mart…"
 * screen and dismisses the moment setReady() arrives.
 */
export class Splash {
  private readonly el: HTMLElement;
  private readonly video: HTMLVideoElement;
  private finished: boolean = false;
  private ready: boolean = false;
  private waiting: boolean = false;
  private resolveDone: (() => void) | undefined;

  constructor(elementId: string) {
    const found: HTMLElement | null = document.getElementById(elementId);
    if (found === null) {
      throw new Error(`Splash element #${elementId} not found`);
    }
    this.el = found;
    const video: HTMLVideoElement | null = this.el.querySelector("video");
    if (video === null) {
      throw new Error(`Splash element #${elementId} has no <video>`);
    }
    this.video = video;
  }

  /** Show the splash and resolve when it fully dismisses (video done AND
   *  world ready). */
  play(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.resolveDone = resolve;
      this.el.hidden = false;

      this.video.addEventListener("ended", () => this.finish());
      this.video.addEventListener("error", () => this.finish());
      this.el.addEventListener("click", () => this.finish());
      window.addEventListener("keydown", this.onKey);

      const attempt: Promise<void> | undefined = this.video.play();
      if (attempt !== undefined) {
        attempt.catch(() => this.finish()); // autoplay refused: skip the video
      }
    });
  }

  /** The world is built and connected; the overlay may lift. */
  setReady(): void {
    this.ready = true;
    if (this.waiting) {
      this.dismiss();
    }
  }

  private onKey = (): void => {
    this.finish();
  };

  private finish(): void {
    if (this.finished) {
      return;
    }
    if (!this.ready) {
      // Video is over but the mall isn't: hold on a loading screen.
      this.showLoadingScreen();
      return;
    }
    this.dismiss();
  }

  private dismiss(): void {
    if (this.finished) {
      return;
    }
    this.finished = true;
    window.removeEventListener("keydown", this.onKey);
    this.video.pause();
    this.el.classList.add("splash-out");
    // Match the CSS fade, then drop the overlay from the DOM entirely.
    setTimeout(() => {
      this.el.hidden = true;
      this.el.remove();
    }, 600);
    this.resolveDone?.();
  }

  /** Loading progress line shown while the overlay waits on the world
   *  (e.g. "Loading Mall Mart… textures 34/41"). No-op after dismissal. */
  setProgress(text: string): void {
    if (this.finished) {
      return;
    }
    const hint: HTMLElement | null = this.el.querySelector(".splash-hint");
    if (hint !== null && this.waiting) {
      hint.textContent = text;
    }
  }

  private showLoadingScreen(): void {
    if (this.waiting) {
      return;
    }
    this.waiting = true;
    this.video.pause();
    this.video.style.display = "none";
    const hint: HTMLElement | null = this.el.querySelector(".splash-hint");
    if (hint !== null) {
      hint.textContent = "Loading Mall Mart…";
    }
  }
}
